import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import BulkClientPasteModal from '../components/BulkClientPasteModal';
import DaumPostcodeModal from '../components/DaumPostcodeModal';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';

const formatPhoneNumber = (value) => {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('82')) {
    digits = digits.slice(2);
    if (digits && !digits.startsWith('0')) {
      digits = `0${digits}`;
    }
  }
  digits = digits.slice(0, 11);
  if (!digits) return '';

  // 대표번호(15xx/16xx/18xx)는 4-4 형식으로 처리 (예: 1600-2012)
  if (/^(15|16|18)\d*$/.test(digits) && digits.length <= 8) {
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  // 서울(02): 02-xxx-xxxx / 02-xxxx-xxxx
  if (digits.startsWith('02')) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, digits.length - 4)}-${digits.slice(-4)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }

  // 휴대폰(010/011/016/017/018/019): 010-xxxx-xxxx
  if (/^01[016789]/.test(digits)) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  }

  // 지역번호(031 등): 031-xxx-xxxx / 031-xxxx-xxxx
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

// 사업자등록번호: 000-00-00000 (10자리)
const formatBusinessRegNo = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const sanitizeEmailInput = (value) => {
  let result = '';
  let atSeen = false;
  for (const ch of String(value || '')) {
    const c = ch.toLowerCase();
    if (/[a-z0-9._+-]/.test(c)) {
      // 로컬/도메인 모두 연속 점(..) 방지
      if (c === '.' && result.endsWith('.')) continue;
      result += c;
    } else if (c === '@' && !atSeen && result.length > 0) {
      result += c;
      atSeen = true;
    }
  }
  return result;
};

const isValidEmailFormat = (value) =>
  /^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i.test(
    String(value || '').trim(),
  );

const getClientEmailError = (email) => {
  const trimmed = String(email || '').trim();
  if (!trimmed) return '대표 이메일을 입력해주세요.';
  if (!isValidEmailFormat(trimmed)) return '올바른 이메일 형식(예: name@domain.com)으로 입력해주세요.';
  return '';
};

const formatClientDisplayName = (row) => {
  const name = String(row?.거래처명 || '');
  if (!row?.거래처명업체명다름) return name;
  const actualName = String(row?.실제업체명 || '').trim();
  if (!actualName) return name;
  const suffix = `(${actualName})`;
  return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
};

// 긴 표기부터 매칭해 "서울특별시" / "서울시" / "서울" 등을 정규화
const KOREA_SIDO_ENTRIES = [
  { canonical: '서울특별시', aliases: ['서울특별시', '서울시', '서울'] },
  { canonical: '부산광역시', aliases: ['부산광역시', '부산시', '부산'] },
  { canonical: '대구광역시', aliases: ['대구광역시', '대구시', '대구'] },
  { canonical: '인천광역시', aliases: ['인천광역시', '인천시', '인천'] },
  { canonical: '광주광역시', aliases: ['광주광역시', '광주시', '광주'] },
  { canonical: '대전광역시', aliases: ['대전광역시', '대전시', '대전'] },
  { canonical: '울산광역시', aliases: ['울산광역시', '울산시', '울산'] },
  { canonical: '세종특별자치시', aliases: ['세종특별자치시', '세종시', '세종'] },
  { canonical: '경기도', aliases: ['경기도', '경기'] },
  { canonical: '강원특별자치도', aliases: ['강원특별자치도', '강원도', '강원'] },
  { canonical: '충청북도', aliases: ['충청북도', '충북'] },
  { canonical: '충청남도', aliases: ['충청남도', '충남'] },
  { canonical: '전북특별자치도', aliases: ['전북특별자치도', '전라북도', '전북'] },
  { canonical: '전라남도', aliases: ['전라남도', '전남'] },
  { canonical: '경상북도', aliases: ['경상북도', '경북'] },
  { canonical: '경상남도', aliases: ['경상남도', '경남'] },
  { canonical: '제주특별자치도', aliases: ['제주특별자치도', '제주도', '제주'] },
];

const parseBusinessAddressRegion = (address) => {
  let text = String(address || '').trim();
  if (!text) return { sido: '', sigungu: '' };

  // 우편번호/괄호 접두어 제거: "(06236) 서울..." / "06236 서울..."
  text = text.replace(/^\(?\d{5}\)?\s*/, '').trim();

  let sido = '';
  let rest = text;
  for (const { canonical, aliases } of KOREA_SIDO_ENTRIES) {
    const sorted = [...aliases].sort((a, b) => b.length - a.length);
    for (const alias of sorted) {
      if (!rest.startsWith(alias)) continue;
      const after = rest.slice(alias.length);
      // 끝 / 공백 / 바로 한글(시군구)로 이어지는 경우만 허용
      if (after !== '' && !after.startsWith(' ') && !/^[가-힣]/.test(after)) continue;
      sido = canonical;
      rest = after.trim();
      break;
    }
    if (sido) break;
  }

  // 시군구: "성남시 분당구", "수원시", "강남구", "가평군"
  let sigungu = '';
  const sigunguMatch = rest.match(/^([가-힣]+시(?:\s+[가-힣]+구)?|[가-힣]+군|[가-힣]+구)/);
  if (sigunguMatch) {
    sigungu = sigunguMatch[1].replace(/\s+/g, ' ').trim();
  }

  return { sido, sigungu };
};

const ClientManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterName, setFilterName] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterSido, setFilterSido] = useState('');
  const [filterSigungu, setFilterSigungu] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientEmailError, setNewClientEmailError] = useState('');
  const [newClientOwner, setNewClientOwner] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientZipCode, setNewClientZipCode] = useState('');
  const [newClientAddress, setNewClientAddress] = useState('');
  const [newClientAddressDetail, setNewClientAddressDetail] = useState('');
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [newClientBusinessRegNo, setNewClientBusinessRegNo] = useState('');
  const [newClientTourismRegNo, setNewClientTourismRegNo] = useState('');
  const [newClientActive, setNewClientActive] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'
  const [actualNamePopover, setActualNamePopover] = useState(null);
  const actualNamePopoverRef = useRef(null);

  const [clientStaffRows, setClientStaffRows] = useState([]);
  const [clientStaffLoading, setClientStaffLoading] = useState(false);
  const [clientStaffError, setClientStaffError] = useState('');
  const [clientStaffOwnership, setClientStaffOwnership] = useState(false);

  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [newStaffIsAdmin, setNewStaffIsAdmin] = useState(true);
  const [newStaffCreating, setNewStaffCreating] = useState(false);
  const [newStaffError, setNewStaffError] = useState('');
  const [isAddStaffModalOpen, setIsAddStaffModalOpen] = useState(false);

  const [isBulkPasteOpen, setIsBulkPasteOpen] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);
  const [bulkProgressText, setBulkProgressText] = useState('');

  const fetchClientList = async () => {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@사용여부1', 값: false },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };

    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to load');
    const data = await res.json();
    return Array.isArray(data?.table) ? data.table : [];
  };

  const fetchClientStaffList = async (clientNo) => {
    const safeClientNo = Number(clientNo);
    if (!Number.isFinite(safeClientNo)) {
      return { rows: [], ownership: false };
    }

    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@거래처번호', 값: safeClientNo },
        { 컬럼명: '@사용여부1', 값: true },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };

    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EC%B2%98_%EC%A7%81%EC%9B%90%EB%A6%AC%EC%8A%A4%ED%8A%B8';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to load staff list');
    const data = await res.json();
    const rows = Array.isArray(data?.table) ? data.table : Array.isArray(data?.Table) ? data.Table : [];
    const ownershipRow = Array.isArray(data?.table1)
      ? data.table1?.[0]
      : Array.isArray(data?.Table1)
        ? data.Table1?.[0]
        : null;
    const ownership = !!ownershipRow?.소유권;
    return { rows, ownership };
  };

  const loadClientStaffList = async (clientNo) => {
    try {
      setClientStaffLoading(true);
      setClientStaffError('');
      const { rows: staffRows, ownership } = await fetchClientStaffList(clientNo);
      setClientStaffRows(Array.isArray(staffRows) ? staffRows : []);
      setClientStaffOwnership(!!ownership);
    } catch (e) {
      setClientStaffRows([]);
      setClientStaffOwnership(false);
      setClientStaffError('직원 리스트를 불러오지 못했습니다.');
    } finally {
      setClientStaffLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const list = await fetchClientList();
      setRows(list);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const resolveCreatedClientNoFromList = (list, { name, email }) => {
    const normalizedName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    if (!normalizedName) return null;
    if (!normalizedEmail) return null;

    const candidates = (Array.isArray(list) ? list : []).filter((r) => {
      const rName = String(r?.거래처명 || '').trim();
      const rEmail = String(r?.대표이메일 || '').trim();
      return rName === normalizedName && rEmail === normalizedEmail;
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => Number(b?.거래처번호 || 0) - Number(a?.거래처번호 || 0));
    const picked = candidates[0];
    const no = Number(picked?.거래처번호);
    return Number.isFinite(no) ? no : null;
  };

  const createClientStaff = async ({ clientNo, loginId, nameKr, phone, isAdmin = true, active = true }) => {
    const safeClientNo = Number(clientNo);
    if (!Number.isFinite(safeClientNo)) {
      throw new Error('거래처번호를 확인할 수 없습니다.');
    }

    const normalizedLoginId = String(loginId || '').trim();
    const normalizedNameKr = String(nameKr || '').trim();
    const normalizedPhone = String(phone || '').trim();

    if (!normalizedLoginId) {
      throw new Error('대표이메일이 없어 직원 아이디를 생성할 수 없습니다.');
    }
    if (!normalizedNameKr) {
      throw new Error('대표자명이 없어 직원 이름을 생성할 수 없습니다.');
    }

    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@등록자', 값: String(userNo ?? '') },
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@거래처번호', 값: safeClientNo },
        { 컬럼명: '@사원번호', 값: 'NULL' },
        { 컬럼명: '@모드', 값: '신규' },
        { 컬럼명: '@아이디', 값: normalizedLoginId },
        { 컬럼명: '@이름', 값: normalizedNameKr },
        { 컬럼명: '@국가번호', 값: '' },
        { 컬럼명: '@휴대폰', 값: normalizedPhone },
        { 컬럼명: '@관리자여부', 값: !!isAdmin },
        { 컬럼명: '@사용여부', 값: !!active },
        { 컬럼명: '@알림수신', 값: false },
      ],
    };

    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_T_%ED%8C%90%EB%A7%A4%EC%B2%98%EC%A7%81%EC%9B%90';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error('거래처 직원이 로그인하여 유저 추가/편집 권한이 거래처로 권한이 넘어갔습니다.');
    }
    const data = await res.json().catch(() => null);
    const message = data?.table?.[0]?.message ?? data?.table?.[0]?.Message ?? data?.message ?? null;
    if (message) {
      const msg = String(message);
      if (msg) throw new Error(msg);
    }
    return data;
  };

  const handleAddClientStaff = async () => {
    if (!editingClient) return;
    const clientNo = Number(editingClient?.거래처번호);
    if (!Number.isFinite(clientNo)) {
      setNewStaffError('거래처번호를 확인할 수 없습니다.');
      return false;
    }

    const email = String(newStaffEmail || '').trim();
    const name = String(newStaffName || '').trim();
    const phone = String(newStaffPhone || '').trim();

    if (!email) {
      setNewStaffError('이메일(로그인 아이디)을 입력해주세요.');
      return false;
    }
    if (!name) {
      setNewStaffError('이름을 입력해주세요.');
      return false;
    }

    try {
      setNewStaffCreating(true);
      setNewStaffError('');
      await createClientStaff({
        clientNo,
        loginId: email,
        nameKr: name,
        phone,
        isAdmin: !!newStaffIsAdmin,
        active: true,
      });
      setNewStaffEmail('');
      setNewStaffName('');
      setNewStaffPhone('');
      setNewStaffIsAdmin(true);
      await loadClientStaffList(clientNo);
      return true;
    } catch (e) {
      const msg = e?.message ? String(e.message) : '';
      setNewStaffError(msg || '직원 추가에 실패했습니다.');
      return false;
    } finally {
      setNewStaffCreating(false);
    }
  };

  const openAddStaffModal = () => {
    setNewStaffEmail('');
    setNewStaffName('');
    setNewStaffPhone('');
    setNewStaffIsAdmin(true);
    setNewStaffCreating(false);
    setNewStaffError('');
    setIsAddStaffModalOpen(true);
  };

  const buildClientUpdatePayload = ({ clientNo, active, fields }) => {
    const { companyNo, userNo } = getUserInfo() || {};
    const f = fields || {
      name: newClientName,
      email: newClientEmail,
      owner: newClientOwner,
      phone: newClientPhone,
      address: newClientAddress,
      addressDetail: newClientAddressDetail,
      zipCode: newClientZipCode,
      businessRegNo: newClientBusinessRegNo,
      tourismRegNo: newClientTourismRegNo,
    };
    return {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@등록자', 값: String(userNo ?? '') },
        { 컬럼명: '@거래처번호', 값: Number(clientNo) },
        { 컬럼명: '@신규업체명', 값: f.name || '' },
        { 컬럼명: '@이메일', 값: f.email || '' },
        { 컬럼명: '@대표자', 값: f.owner || '' },
        { 컬럼명: '@담당자', 값: '' },
        { 컬럼명: '@기본주소', 값: f.address || '' },
        { 컬럼명: '@상세주소', 값: f.addressDetail || '' },
        { 컬럼명: '@우편번호', 값: f.zipCode || '' },
        { 컬럼명: '@국가번호', 값: '' },
        { 컬럼명: '@대표전화', 값: f.phone || '0' },
        { 컬럼명: '@사용여부', 값: !!active },
        { 컬럼명: '@은행코드', 값: '      ' },
        { 컬럼명: '@계좌번호', 값: '' },
        // 예금주/은행명 컬럼을 사업자등록번호/관광사업자등록번호 저장용으로 재사용
        { 컬럼명: '@예금주', 값: f.businessRegNo || '' },
        { 컬럼명: '@은행명', 값: f.tourismRegNo || '' },
        { 컬럼명: '@비고', 값: '' },
        { 컬럼명: '@거래처구분코드', 값: '      ' },
        { 컬럼명: '@첨부파일리스트', 값: '[]' },
        { 컬럼명: '@거래처키워드', 값: '' },
        { 컬럼명: '@미수유예일', 값: 0 },
      ],
    };
  };

  const saveClientInfo = async ({ clientNo, active = true, fields }) => {
    const safeClientNo = Number(clientNo);
    if (!Number.isFinite(safeClientNo)) {
      throw new Error('거래처번호를 확인할 수 없습니다.');
    }
    const payload = buildClientUpdatePayload({ clientNo: safeClientNo, active, fields });
    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98%EC%A0%95%EB%B3%B4%EC%88%98%EC%A0%95';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update');
    await res.json();
  };

  const createClientRecord = async ({
    name,
    email,
    owner,
    phone,
    address = '',
    addressDetail = '',
    zipCode = '',
    businessRegNo = '',
    tourismRegNo = '',
  }) => {
    const normalizedName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const normalizedOwner = String(owner || '').trim();
    const normalizedPhone = String(phone || '').trim();

    if (!normalizedName) throw new Error('거래처명을 입력해주세요.');
    if (!normalizedEmail || !isValidEmailFormat(normalizedEmail)) {
      throw new Error('올바른 대표이메일을 입력해주세요.');
    }
    if (!normalizedOwner || !normalizedPhone) {
      throw new Error('대표자명, 대표전화를 입력해주세요.');
    }

    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@등록자', 값: String(userNo ?? '') },
        { 컬럼명: '@업체번호', 값: 'NULL' },
        { 컬럼명: '@등록기준', 값: '0' },
        { 컬럼명: '@신규업체명', 값: normalizedName },
        { 컬럼명: '@아이디', 값: '' },
        { 컬럼명: '@이메일', 값: normalizedEmail },
        { 컬럼명: '@대표자', 값: normalizedOwner },
        { 컬럼명: '@담당자', 값: '' },
        { 컬럼명: '@기본주소', 값: address || '' },
        { 컬럼명: '@상세주소', 값: addressDetail || '' },
        { 컬럼명: '@우편번호', 값: zipCode || '' },
        { 컬럼명: '@국가번호', 값: '' },
        { 컬럼명: '@대표전화', 값: normalizedPhone || '0' },
        { 컬럼명: '@이메일계정모름', 값: true },
      ],
    };
    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_T_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98%EC%B6%94%EA%B0%80_01';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('거래처 생성에 실패했습니다.');
    const saveData = await res.json().catch(() => null);

    const createdClientNoRaw =
      saveData?.table?.[0]?.거래처번호 ?? saveData?.table?.[0]?.거래처No ?? saveData?.table?.[0]?.id ?? null;
    const createdClientNoParsed = createdClientNoRaw != null ? Number(createdClientNoRaw) : NaN;
    let createdClientNo = Number.isFinite(createdClientNoParsed) ? createdClientNoParsed : null;

    if (createdClientNo == null) {
      try {
        const list = await fetchClientList();
        createdClientNo = resolveCreatedClientNoFromList(list, {
          name: normalizedName,
          email: normalizedEmail,
        });
      } catch (_) {
        createdClientNo = null;
      }
    }

    if (createdClientNo == null) {
      throw new Error('거래처는 생성되었으나 거래처번호를 확인할 수 없습니다.');
    }

    const fields = {
      name: normalizedName,
      email: normalizedEmail,
      owner: normalizedOwner,
      phone: normalizedPhone,
      address: address || '',
      addressDetail: addressDetail || '',
      zipCode: zipCode || '',
      businessRegNo: businessRegNo || '',
      tourismRegNo: tourismRegNo || '',
    };

    try {
      await saveClientInfo({ clientNo: createdClientNo, active: true, fields });
    } catch (e) {
      // 생성은 됐으므로 후속 실패는 경고만
      try {
        console.warn('[ClientManagement] saveClientInfo after create failed', e);
      } catch (_) {}
    }

    try {
      await createClientStaff({
        clientNo: createdClientNo,
        loginId: normalizedEmail,
        nameKr: normalizedOwner,
        phone: normalizedPhone,
      });
    } catch (e) {
      try {
        console.warn('[ClientManagement] createClientStaff after create failed', e);
      } catch (_) {}
    }

    return createdClientNo;
  };

  const handleUpdateClient = async (active) => {
    if (!editingClient) return;
    if (!newClientName) {
      setCreateError('거래처명을 입력해주세요.');
      return;
    }
    if (!validateClientEmailForSave()) return;
    try {
      setCreating(true);
      setCreateError('');
      setNewClientActive(!!active);
      await saveClientInfo({ clientNo: editingClient?.거래처번호, active });
      setIsCreateModalOpen(false);
      setEditingClient(null);
      await fetchData();
    } catch (e) {
      setCreateError('거래처를 수정하지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!actualNamePopover) return undefined;

    const handleClickOutside = (event) => {
      if (actualNamePopoverRef.current?.contains(event.target)) return;
      if (event.target.closest('[data-actual-name-badge]')) return;
      setActualNamePopover(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actualNamePopover]);

  const isEditMode = !!editingClient;

  const hasLoginableClientStaff = useMemo(
    () => clientStaffRows.some((r) => isValidEmailFormat(r?.아이디)),
    [clientStaffRows],
  );

  const showNoLoginableStaffWarning = isEditMode && !clientStaffLoading && !clientStaffError && !hasLoginableClientStaff;

  const handleClientEmailBlur = () => {
    setNewClientEmailError(getClientEmailError(newClientEmail));
  };

  const validateClientEmailForSave = () => {
    const error = getClientEmailError(newClientEmail);
    if (error) {
      setNewClientEmailError(error);
      return false;
    }
    setNewClientEmailError('');
    return true;
  };

  const handleOpenCreateModal = () => {
    setCreateError('');
    setEditingClient(null);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientEmailError('');
    setNewClientOwner('');
    setNewClientPhone('');
    setNewClientZipCode('');
    setNewClientAddress('');
    setNewClientAddressDetail('');
    setIsAddressSearchOpen(false);
    setNewClientBusinessRegNo('');
    setNewClientTourismRegNo('');
    setNewClientActive(true);
    setClientStaffRows([]);
    setClientStaffLoading(false);
    setClientStaffError('');
    setClientStaffOwnership(false);
    setNewStaffEmail('');
    setNewStaffName('');
    setNewStaffPhone('');
    setNewStaffIsAdmin(true);
    setNewStaffCreating(false);
    setNewStaffError('');
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (creating) return;
    setIsCreateModalOpen(false);
    setIsAddStaffModalOpen(false);
    setIsAddressSearchOpen(false);
  };

  const handleOpenEditModal = (client) => {
    setCreateError('');
    setEditingClient(client);
    setNewClientName(formatClientDisplayName(client));
    setNewClientEmail(sanitizeEmailInput(client?.대표이메일 || ''));
    setNewClientEmailError('');
    setNewClientOwner(client?.대표자명 || '');
    setNewClientPhone(formatPhoneNumber(client?.대표전화 || ''));
    setNewClientZipCode(client?.우편번호 || '');
    setNewClientAddress(client?.사업장주소 || client?.기본주소 || '');
    setNewClientAddressDetail(client?.상세주소 || '');
    setIsAddressSearchOpen(false);
    setNewClientBusinessRegNo(formatBusinessRegNo(client?.예금주 || ''));
    setNewClientTourismRegNo(client?.은행명 || '');
    setNewClientActive(!!client?.사용여부);
    setClientStaffRows([]);
    setClientStaffLoading(false);
    setClientStaffError('');
    setClientStaffOwnership(false);
    setNewStaffEmail('');
    setNewStaffName('');
    setNewStaffPhone('');
    setNewStaffIsAdmin(true);
    setNewStaffCreating(false);
    setNewStaffError('');
    setIsAddStaffModalOpen(false);
    setIsCreateModalOpen(true);
    loadClientStaffList(client?.거래처번호);
  };

  const handleCreateClient = async () => {
    if (!newClientName) {
      setCreateError('거래처명을 입력해주세요.');
      return;
    }

    if (!validateClientEmailForSave()) return;

    if (!String(newClientOwner || '').trim() || !String(newClientPhone || '').trim()) {
      setCreateError('대표자명, 대표전화를 입력해주세요.');
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      await createClientRecord({
        name: newClientName,
        email: newClientEmail,
        owner: newClientOwner,
        phone: newClientPhone,
        address: newClientAddress,
        addressDetail: newClientAddressDetail,
        zipCode: newClientZipCode,
        businessRegNo: newClientBusinessRegNo,
        tourismRegNo: newClientTourismRegNo,
      });
      setIsCreateModalOpen(false);
      await fetchData();
    } catch (e) {
      setCreateError(e?.message ? String(e.message) : '거래처를 추가하지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleBulkPasteConfirm = async (pasteRows) => {
    const list = Array.isArray(pasteRows) ? pasteRows : [];
    if (!list.length) return;

    setBulkCreating(true);
    setBulkProgressText(`0 / ${list.length} 등록 중...`);

    let success = 0;
    const failures = [];

    for (let i = 0; i < list.length; i += 1) {
      const row = list[i] || {};
      setBulkProgressText(`${i + 1} / ${list.length} 등록 중... (${row.거래처명 || ''})`);
      try {
        await createClientRecord({
          name: row.거래처명,
          email: row.대표이메일,
          owner: row.대표자명,
          phone: formatPhoneNumber(row.대표전화),
          address: row.주소 || '',
          addressDetail: row.상세주소 || '',
          zipCode: row.우편번호 || '',
          businessRegNo: formatBusinessRegNo(row.사업자등록번호),
          tourismRegNo: row.관광사업자등록번호 || '',
        });
        success += 1;
      } catch (e) {
        failures.push(`${i + 1}. ${row.거래처명 || '(이름없음)'}: ${e?.message ? String(e.message) : '실패'}`);
      }
    }

    setBulkCreating(false);
    setBulkProgressText('');
    await fetchData();

    if (failures.length === 0) {
      setIsBulkPasteOpen(false);
      window.alert(`${success}건의 거래처가 등록되었습니다.`);
      return;
    }

    window.alert(
      `성공 ${success}건 / 실패 ${failures.length}건\n\n${failures.slice(0, 12).join('\n')}${
        failures.length > 12 ? '\n...' : ''
      }`,
    );
  };

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      const nameOk = filterName ? String(r?.거래처명 || '').toLowerCase().includes(filterName.toLowerCase()) : true;
      const mgrOk = filterManager ? String(r?.담당자명 || '').toLowerCase().includes(filterManager.toLowerCase()) : true;
      const phoneOk = filterPhone ? String(r?.대표전화 || '').toLowerCase().includes(filterPhone.toLowerCase()) : true;
      const emailOk = filterEmail ? String(r?.대표이메일 || '').toLowerCase().includes(filterEmail.toLowerCase()) : true;
      let regionOk = true;
      if (filterSido || filterSigungu) {
        const { sido, sigungu } = parseBusinessAddressRegion(r?.사업장주소);
        const sidoOk = filterSido ? sido === filterSido : true;
        const sigunguOk = filterSigungu ? sigungu === filterSigungu : true;
        regionOk = sidoOk && sigunguOk;
      }
      return nameOk && mgrOk && phoneOk && emailOk && regionOk;
    });
  }, [rows, filterName, filterManager, filterPhone, filterEmail, filterSido, filterSigungu]);

  const addressRegions = useMemo(() => {
    const sidoSet = new Set();
    const sigunguBySido = new Map();
    rows.forEach((r) => {
      const { sido, sigungu } = parseBusinessAddressRegion(r?.사업장주소);
      if (!sido) return;
      sidoSet.add(sido);
      if (!sigungu) return;
      if (!sigunguBySido.has(sido)) sigunguBySido.set(sido, new Set());
      sigunguBySido.get(sido).add(sigungu);
    });
    const sidoOrder = KOREA_SIDO_ENTRIES.map((e) => e.canonical);
    const sidoOptions = [...sidoSet].sort((a, b) => {
      const ai = sidoOrder.indexOf(a);
      const bi = sidoOrder.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi) || a.localeCompare(b, 'ko');
    });
    return { sidoOptions, sigunguBySido };
  }, [rows]);

  const sigunguOptions = useMemo(() => {
    if (!filterSido) return [];
    const set = addressRegions.sigunguBySido.get(filterSido);
    return set ? [...set].sort((a, b) => a.localeCompare(b, 'ko')) : [];
  }, [addressRegions, filterSido]);

  const displayedRows = useMemo(() => {
    const showActive = activeTab === 'active';
    return filteredRows.filter(r => {
      const isActive = !!r?.사용여부;
      return showActive ? isActive : !isActive;
    });
  }, [filteredRows, activeTab]);

  const handleExportExcel = () => {
    try {
      if (!displayedRows.length) {
        window.alert('내보낼 거래처 데이터가 없습니다.');
        return;
      }

      const exportRows = displayedRows.map((r, index) => ({
        'No': index + 1,
        '거래처번호': r?.거래처번호 ?? '',
        '거래처명': formatClientDisplayName(r),
        '담당자명': r?.대표자명 ?? r?.담당자명 ?? '',
        '이메일': r?.대표이메일 ?? r?.이메일 ?? '',
        '전화번호': r?.대표전화 ?? r?.전화번호 ?? '',
        '주소': r?.사업장주소 ?? '',
        '사용여부': r?.사용여부 ? '활성' : '비활성',
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '거래처');

      const today = new Date();
      const ymd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const tabLabel = activeTab === 'active' ? '활성' : '비활성';
      XLSX.writeFile(workbook, `거래처목록_${tabLabel}_${ymd}.xlsx`);
    } catch (error) {
      try { console.error('[ClientManagement] export excel failed', error); } catch (_) {}
      window.alert('엑셀 내보내기에 실패했습니다.');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-2xl bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-[17px] font-bold text-gray-900">거래처 관리</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
            onClick={handleExportExcel}
          >
            엑셀 내보내기
          </button>
          <button
            type="button"
            className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
            onClick={() => setIsBulkPasteOpen(true)}
          >
            일괄등록
          </button>
          <button
            type="button"
            className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"
            onClick={handleOpenCreateModal}
          >
            신규 거래처 추가
          </button>
        </div>
      </div>

      <div className="mb-2 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
          placeholder="거래처명 검색"
        />
        <input
          value={filterManager}
          onChange={(e) => setFilterManager(e.target.value)}
          className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
          placeholder="담당자명 검색"
        />
        <input
          value={filterPhone}
          onChange={(e) => setFilterPhone(e.target.value)}
          className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
          placeholder="전화번호 검색"
        />
        <div className="flex gap-2">
          <input
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
            placeholder="이메일 검색"
          />
          <button
            type="button"
            className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50 whitespace-nowrap"
            onClick={() => {
              setFilterName('');
              setFilterManager('');
              setFilterPhone('');
              setFilterEmail('');
              setFilterSido('');
              setFilterSigungu('');
            }}
          >
            초기화
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <select
          value={filterSido}
          onChange={(e) => {
            setFilterSido(e.target.value);
            setFilterSigungu('');
          }}
          className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
        >
          <option value="">시도 전체</option>
          {addressRegions.sidoOptions.map((sido) => (
            <option key={sido} value={sido}>{sido}</option>
          ))}
        </select>
        <select
          value={filterSigungu}
          onChange={(e) => setFilterSigungu(e.target.value)}
          disabled={!filterSido}
          className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none disabled:bg-gray-100 disabled:text-gray-400"
        >
          <option value="">{filterSido ? '시군구 전체' : '시도를 먼저 선택'}</option>
          {sigunguOptions.map((sigungu) => (
            <option key={sigungu} value={sigungu}>{sigungu}</option>
          ))}
        </select>
      </div>
      </div>

      {loading && (
        <div className="rounded-2xl bg-white p-5 py-16 text-center text-sm text-gray-500">로딩 중...</div>
      )}
      {!loading && error && (
        <div className="rounded-2xl bg-white p-5 py-16 text-center text-sm text-red-600">{error}</div>
      )}
      {!loading && !error && (
        <div className="rounded-2xl bg-white p-5">
          <div className="mb-4 inline-flex gap-1 rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm font-semibold rounded-md ${activeTab === 'active' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
              onClick={() => setActiveTab('active')}
            >
              활성 거래처
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm font-semibold rounded-md ${activeTab === 'inactive' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
              onClick={() => setActiveTab('inactive')}
            >
              비활성 거래처
            </button>
          </div>
          <div className="overflow-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">거래처명</th>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">대표자명</th>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">대표이메일</th>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">대표전화</th>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">사업장주소</th>
                <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">사용여부</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {displayedRows.map((r, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenEditModal(r)}
                >
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>{formatClientDisplayName(r)}</span>
                      {!!r?.거래처명업체명다름 && String(r?.실제업체명 || '').trim() && (
                        <button
                          type="button"
                          data-actual-name-badge
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            const key = r?.거래처번호 ?? idx;
                            const rect = e.currentTarget.getBoundingClientRect();
                            const actualName = String(r?.실제업체명 || '').trim();
                            setActualNamePopover((prev) => (
                              prev?.key === key
                                ? null
                                : {
                                    key,
                                    name: actualName,
                                    top: rect.bottom + 6,
                                    left: rect.left,
                                  }
                            ));
                          }}
                        >
                          실제업체명
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{r?.대표자명 ?? ''}</td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{r?.대표이메일 ?? ''}</td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{r?.대표전화 ?? ''}</td>
                  <td className="px-3 py-3 text-sm text-gray-700">{r?.사업장주소 ?? ''}</td>
                  <td className="px-3 py-3 text-sm whitespace-nowrap"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${r?.사용여부 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{r?.사용여부 ? '활성' : '비활성'}</span></td>
                </tr>
              ))}
              {displayedRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-sm text-gray-500">표시할 데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {actualNamePopover && (
        <div
          ref={actualNamePopoverRef}
          className="fixed z-50 min-w-[10rem] max-w-xs rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs text-gray-700 shadow-md"
          style={{ top: actualNamePopover.top, left: actualNamePopover.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] font-medium text-gray-500 mb-0.5">실제 업체명</div>
          <div className="font-medium text-gray-900">{actualNamePopover.name}</div>
        </div>
      )}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="shrink-0 px-6 pt-5 pb-3">
              <h2 className="text-[17px] font-bold">{isEditMode ? '거래처 편집' : '신규 거래처 추가'}</h2>
            </div>
            {showNoLoginableStaffWarning && (
              <div className="shrink-0 mx-6 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                정상적으로 로그인이 가능한 거래처 직원이 없습니다. 거래처 직원 리스트의 아이디(이메일)를 확인해주세요.
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
            <div className="space-y-3">
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">거래처명</label>
                <input
                  className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="거래처명을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">
                  대표이메일(거래처 아이디로 자동 등록됩니다.이메일 양식으로 입력해 주세요)
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  pattern="[a-zA-Z0-9._+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                  title="이메일 형식(예: name@domain.com)으로 입력해주세요"
                  className={`w-full h-9 rounded-lg border bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none ${newClientEmailError ? 'border-red-500' : 'border-gray-300'}`}
                  value={newClientEmail}
                  onChange={(e) => {
                    const next = sanitizeEmailInput(e.target.value);
                    setNewClientEmail(next);
                    if (!next) {
                      setNewClientEmailError('');
                      return;
                    }
                    if (isValidEmailFormat(next)) {
                      setNewClientEmailError('');
                    } else if (newClientEmailError || /@.+\.[a-z0-9-]*$/i.test(next)) {
                      setNewClientEmailError('올바른 이메일 형식(예: name@domain.com)으로 입력해주세요.');
                    }
                  }}
                  onBlur={handleClientEmailBlur}
                  placeholder="name@domain.com"
                />
                {newClientEmailError && (
                  <p className="mt-1 text-xs text-red-600">{newClientEmailError}</p>
                )}
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">대표자</label>
                <input
                  className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientOwner}
                  onChange={(e) => setNewClientOwner(e.target.value)}
                  placeholder="대표자명을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">대표전화</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(formatPhoneNumber(e.target.value))}
                  placeholder="010-1234-5678"
                  maxLength={13}
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">주소</label>
                <div className="flex gap-2">
                  <input
                    className="w-28 shrink-0 h-9 rounded-lg border border-gray-300 bg-gray-50 px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                    value={newClientZipCode}
                    readOnly
                    placeholder="우편번호"
                  />
                  <button
                    type="button"
                    className="shrink-0 h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                    onClick={() => setIsAddressSearchOpen(true)}
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  className="mt-2 w-full h-9 rounded-lg border border-gray-300 bg-gray-50 px-2.5 text-sm cursor-pointer focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientAddress}
                  readOnly
                  placeholder="주소 검색으로 입력하세요"
                  onClick={() => setIsAddressSearchOpen(true)}
                />
                <input
                  className="mt-2 w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientAddressDetail}
                  onChange={(e) => setNewClientAddressDetail(e.target.value)}
                  placeholder="상세주소 (동/호수 등)"
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">사업자등록번호</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientBusinessRegNo}
                  onChange={(e) => setNewClientBusinessRegNo(formatBusinessRegNo(e.target.value))}
                  placeholder="000-00-00000"
                  maxLength={12}
                />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1">관광사업자등록번호</label>
                <input
                  className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                  value={newClientTourismRegNo}
                  onChange={(e) => setNewClientTourismRegNo(e.target.value)}
                  placeholder="관광사업자등록번호를 입력하세요"
                />
              </div>
              {isEditMode && (
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1">사용여부</label>
                  <div className="flex items-center gap-4 text-sm text-gray-700">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="client-active"
                        className="mr-2"
                        checked={!!newClientActive}
                        onChange={() => setNewClientActive(true)}
                      />
                      활성
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="client-active"
                        className="mr-2"
                        checked={!newClientActive}
                        onChange={() => setNewClientActive(false)}
                      />
                      비활성
                    </label>
                  </div>
                </div>
              )}
              {createError && (
                <div className="text-xs text-red-600">{createError}</div>
              )}
            </div>
            {isEditMode && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">거래처 직원 리스트</h3>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500">{clientStaffOwnership ? '소유권 있음' : ''}</div>
                    <button
                      type="button"
                      className="h-8 rounded-lg px-3 text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                      onClick={openAddStaffModal}
                    >
                      직원 추가
                    </button>
                  </div>
                </div>
                <div className="h-64 overflow-y-auto overflow-x-hidden border border-gray-200 rounded-xl">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">아이디</th>
                        <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">직원명</th>
                        <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">관리자 여부</th>
                        <th className="h-10 bg-gray-100 px-3 text-left text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg">휴대폰</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {clientStaffLoading && (
                        <tr>
                          <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">로딩 중...</td>
                        </tr>
                      )}
                      {!clientStaffLoading && clientStaffError && (
                        <tr>
                          <td colSpan={4} className="px-3 py-6 text-center text-sm text-red-600">{clientStaffError}</td>
                        </tr>
                      )}
                      {!clientStaffLoading && !clientStaffError && clientStaffRows.map((r, idx) => (
                        <tr key={idx} className="border-b border-gray-200">
                          <td className="px-3 py-2 text-sm text-gray-900 break-all">{r?.아이디 ?? ''}</td>
                          <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{r?.직원명 ?? ''}</td>
                          <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{r?.관리자 ? '예' : '아니오'}</td>
                          <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{r?.휴대폰 ?? ''}</td>
                        </tr>
                      ))}
                      {!clientStaffLoading && !clientStaffError && clientStaffRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">표시할 데이터가 없습니다.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isEditMode && isAddStaffModalOpen && (
              <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40">
                <div className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-lg p-6">
                  <h3 className="text-[17px] font-bold mb-3">직원 추가</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-600 mb-1">이메일(로그인 아이디)</label>
                      <input
                        type="email"
                        className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                        value={newStaffEmail}
                        onChange={(e) => setNewStaffEmail(e.target.value)}
                        placeholder="example@domain.com"
                        disabled={newStaffCreating}
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-600 mb-1">이름</label>
                      <input
                        className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                        value={newStaffName}
                        onChange={(e) => setNewStaffName(e.target.value)}
                        placeholder="직원명"
                        disabled={newStaffCreating}
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-600 mb-1">휴대폰</label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                        value={newStaffPhone}
                        onChange={(e) => setNewStaffPhone(formatPhoneNumber(e.target.value))}
                        placeholder="010-1234-5678"
                        maxLength={13}
                        disabled={newStaffCreating}
                      />
                    </div>
                    <label className="inline-flex items-center text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={!!newStaffIsAdmin}
                        onChange={(e) => setNewStaffIsAdmin(e.target.checked)}
                        disabled={newStaffCreating}
                      />
                      관리자 여부
                    </label>
                    {newStaffError && (
                      <div className="text-xs text-red-600">{newStaffError}</div>
                    )}
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => setIsAddStaffModalOpen(false)}
                      disabled={newStaffCreating}
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                      onClick={async () => {
                        const ok = await handleAddClientStaff();
                        if (ok) setIsAddStaffModalOpen(false);
                      }}
                      disabled={newStaffCreating}
                    >
                      {newStaffCreating ? '추가 중...' : '추가'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            </div>
            <div className="shrink-0 px-6 pb-5 pt-3 flex justify-end gap-2 bg-white">
              <button
                type="button"
                className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                onClick={handleCloseCreateModal}
                disabled={creating}
              >
                취소
              </button>
              <button
                type="button"
                className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={isEditMode ? () => handleUpdateClient(newClientActive) : handleCreateClient}
                disabled={creating || !isValidEmailFormat(newClientEmail) || !!newClientEmailError}
              >
                {creating ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
      <DaumPostcodeModal
        open={isAddressSearchOpen}
        onClose={() => setIsAddressSearchOpen(false)}
        onSelect={({ zonecode, address }) => {
          setNewClientZipCode(zonecode || '');
          setNewClientAddress(address || '');
        }}
      />
      <BulkClientPasteModal
        open={isBulkPasteOpen}
        onClose={() => {
          if (bulkCreating) return;
          setIsBulkPasteOpen(false);
          setBulkProgressText('');
        }}
        onConfirm={handleBulkPasteConfirm}
        confirming={bulkCreating}
        progressText={bulkProgressText}
        existingEmails={rows.map((r) => r?.대표이메일 || r?.이메일 || '')}
      />
    </div>
  );
};

export default ClientManagement;
